import dayjs from "dayjs";
import { CalendarIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { MonthCalendar } from "@/components/ActivityCalendar";
import { useDateFilterNavigation } from "@/hooks";
import { useActivityStats } from "@/hooks/useActivityStats";
import { useTranslate } from "@/utils/i18n";
import { MonthNavigator } from "./MonthNavigator";

const StatisticsView = () => {
  const navigateToDateFilter = useDateFilterNavigation();
  const t = useTranslate();
  const [visibleMonthString, setVisibleMonthString] = useState(dayjs().format("YYYY-MM"));

  // Load full year data for both month and year calendar views
  const selectedYear = parseInt(visibleMonthString.substring(0, 4));
  const startDate = dayjs().year(selectedYear).startOf("year").format("YYYY-MM-DD");
  const endDate = dayjs().year(selectedYear).endOf("year").format("YYYY-MM-DD");

  const { data: activityData } = useActivityStats({
    startDate,
    endDate,
  });

  const activityStats = activityData?.activityStats || {};
  const holidays = activityData?.holidays || {};

  const handleMonthChange = (newMonth: string) => {
    setVisibleMonthString(newMonth);
  };

  const maxCount = useMemo(() => {
    const counts = Object.values(activityStats);
    return Math.max(...counts, 1);
  }, [activityStats]);

  const totalActivity = useMemo(() => {
    return Object.values(activityStats).reduce((sum, count) => sum + count, 0);
  }, [activityStats]);

  const activeDays = useMemo(() => {
    return Object.values(activityStats).filter((count) => count > 0).length;
  }, [activityStats]);

  return (
    <div className="group w-full flex flex-col">
      {/* Animated background pattern */}
      <div className="relative mb-3">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-amber-500/5 rounded-3xl blur-2xl" />

        {/* Main glass card */}
        <div className="relative">
          <div className="glass-card rounded-2xl p-4 space-y-4 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/5 hover:border-purple-500/20">
            {/* Header with Activity stats */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Icon container with gradient */}
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-50 to-amber-50 dark:from-purple-950/5 dark:to-amber-950/5 border border-purple-200/50 dark:border-purple-800/10 flex items-center justify-center shadow-sm">
                  <CalendarIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>

                {/* Activity stats summary */}
                <div className="flex flex-col">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{t("common.calendar")}</span>
                  <span className="text-xs font-semibold text-foreground">
                    {activeDays} <span className="text-muted-foreground font-normal">{t("common.days")}</span>
                  </span>
                </div>
              </div>

              {/* Total count badge */}
              <div className="relative px-2 py-1 rounded-lg bg-gradient-to-br from-purple-100 to-amber-100 dark:from-purple-950/10 dark:to-amber-950/10 border border-purple-200/50 dark:border-purple-800/10">
                <span className="text-xs font-bold bg-gradient-to-r from-purple-600 to-amber-600 dark:from-purple-400 dark:to-amber-400 bg-clip-text text-transparent">
                  {totalActivity}
                </span>
                {/* Glow effect on hover */}
                <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-purple-500/10 to-amber-500/10 dark:from-purple-500/5 dark:to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>

            {/* Month navigator */}
            <div className="flex items-center justify-center">
              <MonthNavigator
                visibleMonth={visibleMonthString}
                onMonthChange={handleMonthChange}
                activityStats={activityStats}
                holidays={holidays}
              />
            </div>

            {/* Calendar with staggered animation */}
            <div className="w-full animate-scale-in">
              <div className="relative rounded-xl overflow-hidden">
                {/* Subtle inner glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-amber-500/5" />

                <MonthCalendar
                  month={visibleMonthString}
                  data={activityStats}
                  maxCount={maxCount}
                  onClick={navigateToDateFilter}
                  holidays={holidays}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsView;
